import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { LoginService } from '../login-service/login.service';
import { filter, finalize, take, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { MsalInitService } from '../msal-init.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();
  private sessionCheckInterval: any;
  private sessionTimeout = 30 * 60 * 1000; // 30 minutes in miliseconds
  private readonly _destroying$ = new Subject<void>();
  private isProcessingAuthentication = false;

  private redirectURL = environment.msalConfig.auth.redirectUri;

  constructor(
    private msalService: MsalService,
    private router: Router,
    private loginService: LoginService,
    private toastService: ToastrService,
    private msalInitService: MsalInitService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    const storage = this.getSessionStorage();
    const isLoggedIn = storage?.getItem('isLoggedIn') === 'true';
    this.isLoggedInSubject.next(isLoggedIn);
    this.startSessionTimer();
  }

  public handleRedirectCallback(): void {
    if (this.isProcessingAuthentication) return;

    this.msalInitService
      .ensureInitialized()
      .then(() => {
        this.isProcessingAuthentication = true;
        this.msalService
          .handleRedirectObservable()
          .pipe(
            takeUntil(this._destroying$),
            filter((result) => !!result),
            take(1)
          )
          .subscribe({
            next: (result: AuthenticationResult) => {
              if (result) {
                this.processAuthentication(result);
                // this.router.navigate(['/dashboard']);
              }
            },
            error: (error) => {
              this.toastService.error(`MSAL Redirect error: ${error}`);
              this.router.navigate(['/login']);
            },
          });
      })
      .catch((error) => {
        this.router.navigate(['/login']);
      });
  }

  // Update the login state and persist it in sessionStorage
  private updateLoginState(isLoggedIn: boolean): void {
    this.isLoggedInSubject.next(isLoggedIn);
    const storage = this.getSessionStorage();
    if (storage) {
      storage.setItem('isLoggedIn', isLoggedIn.toString());
    }
  }

  private getSessionStorage(): Storage | null | undefined {
    if (isPlatformBrowser(this.platformId)) {
      return sessionStorage;
    }
    return null;
  }

  private startSessionTimer(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }

    this.sessionCheckInterval = setInterval(() => {
      this.checkSessionValidity();
    }, 60000);
  }

  private checkSessionValidity(): void {
    const storage = this.getSessionStorage();
    if (!storage) return;

    const sessionTimestamp = storage.getItem('sessionTimestamp');
    if (!sessionTimestamp) return;

    const currentTime = new Date().getTime();
    const sessionTime = parseInt(sessionTimestamp, 10);

    if (currentTime - sessionTime > this.sessionTimeout) {
      this.toastService.error('Your session has expired. Please login again');
      this.logout();
    }
  }

  public processAuthentication(result: AuthenticationResult): void {
    this.loginService.loaderSubject.next(true);
    const storage = this.getSessionStorage();
    if (!storage) return;

    const selectedNode = storage.getItem('selectedNode');
    const selectedEnv = storage.getItem('selectedEnv');
    this.loginService
      .getLoginDetails(
        result.account.username,
        result.accessToken,
        this.redirectURL,
        selectedNode ?? undefined, // Convert null to undefined
        selectedEnv ?? undefined // Convert null to undefined
      )
      .pipe(
        takeUntil(this._destroying$),
        finalize(() => this.loginService.loaderSubject.next(false))
      )
      .subscribe({
        next: (response: any) => {
          this.toastService.success('', `Login Successful`);

          this.updateLoginState(true);

          // Store the account information
          this.msalService.instance.setActiveAccount(result.account);

          sessionStorage.setItem(
            'user_details',
            JSON.stringify(response.token_info)
          );
          storage.setItem('Session_Id', response.session_id);
          storage.setItem('sessionTimestamp', new Date().getTime().toString());

          this.startSessionTimer();
          this.router.navigate(['/dashboard']);
        },
         error: (error: any) => {
          this.updateLoginState(false);
          if(!error.error.detail || error.error.detail == 'undefined'){
             this.toastService.error(
            `Login Failed. Please contact your admin.`
          );
          }else{
            this.toastService.error(
              `Snowflake SSO Login Failed: ${error.error.detail}`
            );
          }
          // Sign out from Azure AD since Snowflake SSO failed
          this.router.navigate(['/login']);
        },
      });
  }

  // Call this method during logout
  public logout(): void {
    this.updateLoginState(false); // Set login state to false
    const storage = this.getSessionStorage();
    if (!storage) return;

    const sessionID = storage.getItem('Session_Id');
    if (sessionID) {
      this.loginService.appLogOut({ session_id: sessionID }).subscribe({
        next: () => {
          this.clearSession();
          this.msalService.logoutRedirect({
            postLogoutRedirectUri: window.location.origin + '/login',
          });
        },
        error: (error) => {
          this.clearSession();
          this.msalService.logoutRedirect({
            postLogoutRedirectUri: window.location.origin + '/login',
          });
        },
      });
    } else {
      this.clearSession();
      this.msalService.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/login',
      });
    }
  }

  public clearSession(): void {
    const storage = this.getSessionStorage();
    if (!storage) return;

    storage.removeItem('Session_Id');
    storage.removeItem('sessionTimestamp');
    storage.removeItem('selectedNode');
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
  }

  ngOnDestroy() {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
